import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { clearAvailabilityCache, useSessionPicker } from '../../hooks/booking/useSessionPicker'
import { useCheckoutApi } from '../../hooks/checkout/useCheckoutApi'
import { canReschedule } from '../../hooks/booking/useBookingCancellation'
import { useRefundPoints } from '../../hooks/refundPoints/useRefundPoints'

import PageHeader from '../../components/ui/pageHeader/PageHeader'
import SummaryPanel from '../../components/authenticated/booking/summaryPanel/SummaryPanel'
import BookingLegend from '../../components/authenticated/booking/bookingLegend/BookingLegend'
import SessionPicker, { SessionPickerDays, SessionPickerSetup } from '../../components/authenticated/booking/sessionPicker/SessionPicker'
import BookingPageLayout from '../../components/bookingPageLayout/BookingPageLayout'

import { useApp } from '../../contexts/AppContext'
import { useConfirm } from '../../contexts/ConfirmContext'
import { BannerKind } from '../../types/contexts/AppContext'
import { Booking, BookingFunding } from '../../types/booking/booking'
import { CONFIRM_SKIP_NAVIGATION, ConfirmLayout } from '../../types/confirm/confirm'
import {
  attemptRequestId,
  attemptSignature,
  checkoutPath,
  formatMinutes,
  formatMoney,
  formatPoints,
  sessionKey,
  timeOf,
} from '../../utils/booking/booking'
import { toDisplayOnlyDate } from '../../utils/dateUtils'
import { applicablePoints } from '../../utils/booking/refundPoints'

type RescheduleState = { booking?: Booking }

/**
 * Moves a confirmed booking. The original booking stays reserved until the move is finalised; prepaid minutes are
 * re-checked for the new time, a more expensive time is paid with refund points first and the rest through a normal
 * payment hold, a cheaper one is not partially refunded. The reschedule counter only increases when the move succeeds.
 */
const Reschedule = () => {
  const { t, i18n } = useTranslation(['history', 'bookings'])
  const navigate = useNavigate()
  const location = useLocation()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { reschedule } = useCheckoutApi()
  const [booking] = useState(() => (location.state as RescheduleState | null)?.booking)
  const { summary } = useRefundPoints()
  const [availablePoints, setAvailablePoints] = useState(0)

  useEffect(() => {
    summary()
      .then(result => setAvailablePoints(result?.available ?? 0))
      .catch(() => {
        // Previewed without points; the server applies them on the move anyway.
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const picker = useSessionPicker({
    single: true,
    rescheduledBookingId: booking?.id,
    initial: booking
      ? {
          clubId: booking.clubId,
          durationMinutes: booking.durationMinutes,
          day: new Date(booking.startTime).toISOString(),
        }
      : undefined,
  })

  useEffect(() => {
    if (picker.error) {
      showBanner({ kind: BannerKind.ERR, msg: picker.error })
      picker.resetError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.error])

  if (!booking || !canReschedule(booking)) {
    return <Navigate to="/history" replace />
  }

  const destination = picker.selected[0]
  const prepaid = booking.funding === BookingFunding.PASS
  const lines = destination
    ? [
        {
          key: sessionKey(destination),
          courtName: destination.courtName,
          startTime: destination.startTime,
          durationMinutes: destination.durationMinutes,
          amount: destination.price,
          prepaidMinutes: prepaid ? destination.durationMinutes : 0,
        },
      ]
    : []
  const difference =
    !prepaid && destination?.price && booking.price
      ? Math.max(0, destination.price.amount - booking.price.amount)
      : 0
  const points = applicablePoints(difference, availablePoints)
  const currency = destination?.price?.currency ?? booking.price?.currency ?? 'ron'
  const total = destination ? { amount: Math.round((difference - points) * 100) / 100, currency } : undefined

  const describe = (startTime: string, endTime: string, courtName: string) =>
    `${courtName} · ${toDisplayOnlyDate(startTime, i18n.language)} ${timeOf(startTime)}–${timeOf(endTime)}`

  const handleConfirm = () => {
    if (!destination) {
      return
    }
    const signature = attemptSignature(['reschedule', booking.id, sessionKey(destination), destination.durationMinutes])
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('history.reschedule.confirm-title'),
      subtitle: t('history.reschedule.confirm-subtitle', {
        used: booking.rescheduleCount + 1,
        max: booking.maxReschedules,
      }),
      itemCards: [
        {
          id: 'move',
          title: t('history.reschedule.move'),
          details: [
            {
              label: t('history.reschedule.from'),
              value: describe(booking.startTime, booking.endTime, booking.courtName),
            },
            {
              label: t('history.reschedule.to'),
              value: describe(destination.startTime, destination.endTime, destination.courtName),
            },
            {
              label: t('history.reschedule.duration'),
              value: formatMinutes(destination.durationMinutes),
            },
          ],
        },
      ],
      summary: {
        details:
          points > 0
            ? [
                {
                  label: t('history.reschedule.difference'),
                  value: formatMoney({ amount: difference, currency }),
                },
                {
                  label: t('bookings:booking.booking-courts.summary-panel.refund-points'),
                  value: t('bookings:booking.booking-courts.summary-panel.refund-points-value', {
                    points: formatPoints(points),
                  }),
                },
              ]
            : [],
        total: { label: t('history.reschedule.to-pay'), value: formatMoney(total) },
      },
      warning: prepaid
        ? t('history.reschedule.prepaid-note')
        : (total?.amount ?? 0) > 0
          ? t('history.reschedule.difference-note', { amount: formatMoney(total) })
          : difference > 0
            ? t('history.reschedule.points-note')
            : t('history.reschedule.no-refund-note'),
      confirmLabel: t('history.reschedule.confirm'),
      backLabel: t('bookings:booking-confirmation.back-label'),
      returnTo: '/history',
      onConfirm: async () => {
        const checkout = await reschedule(booking.id, {
          clientRequestId: attemptRequestId(signature),
          courtId: destination.courtId,
          startTime: destination.startTime,
          durationMinutes: destination.durationMinutes,
        })
        if (checkout) {
          clearAvailabilityCache()
          navigate(checkoutPath(checkout.holdId), { replace: true })
        }
        return CONFIRM_SKIP_NAVIGATION
      },
    })
  }

  return (
    <BookingPageLayout
      header={
        <PageHeader
          title={{
            eyebrow: t('history.reschedule.eyebrow', {
              used: booking.rescheduleCount,
              max: booking.maxReschedules,
            }),
            h1: t('history.reschedule.title'),
          }}
          sub={t('history.reschedule.sub', {
            current: describe(booking.startTime, booking.endTime, booking.courtName),
          })}
        />
      }
      toolbar={<SessionPickerSetup picker={picker} />}
      days={<SessionPickerDays picker={picker} />}
      courts={<SessionPicker picker={picker} showDays={false} />}
      legend={<BookingLegend />}
      summary={
        <SummaryPanel
          lines={lines}
          total={total}
          pointsAmount={points}
          showPrepaid={prepaid}
          confirmLabel={t('history.reschedule.continue')}
          confirmDisabled={!destination}
          totalSubtitle={t('history.reschedule.to-pay')}
          onConfirm={handleConfirm}
          onClear={picker.clear}
        />
      }
    />
  )
}

export default Reschedule
